import { useContext } from "react";
import { Modal, Table, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

import useTabExternalSupport from "./useTabExternalSupport";
import ActiveFiltersBar from "../../../../../components/ActiveFiltersBar";
import TableCard from "../../../../../components/TableCard";
import TableDicaHint from "../../../../../components/TableDicaHint";
import TablePagination from "../../../../../components/TablePagination";
import { ThemeContext } from "../../../../../contexts/ThemeContext";
import ModalUpdateSupport from "../../../../Support/components/ModalUpdateSupport";

const { Paragraph } = Typography;

interface ITabExternalSupportProps {
    origin: "Aplicativo" | "Site";
}

export default function TabExternalSupport({ origin }: ITabExternalSupportProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        isLoading,
        columns,
        tableData,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        handleClickViewSupport,
        supportBeingDeleted,
        isDeleteModalVisible,
        isLoadingDelete,
        handleCancelDelete,
        handleConfirmDelete,
        supportBeingUpdated,
        isUpdateModalVisible,
        isLoadingUpdate,
        handleCloseUpdateModal,
        handleSaveUpdateSupport,
    } = useTabExternalSupport({ origin });

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Active Filters Bar */}
            <ActiveFiltersBar
                filters={activeFilterItems}
                onClearAll={handleClearAllFilters}
            />

            <TableDicaHint text="Dê um clique em qualquer linha ou utilize o botão de visualização para conferir os detalhes do contato." />

            <TablePagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePaginationChange}
                itemName="mensagens"
                pageSizeOptions={["10", "25", "50", "100"]}
            />

            {/* Table Container */}
            <TableCard>
                <Table
                    rowKey="id"
                    size="middle"
                    className={`modern-data-table ${isDark ? "modern-data-table-dark" : ""}`}
                    columns={columns}
                    dataSource={tableData}
                    loading={isLoading}
                    pagination={false}
                    onChange={handleTableChange}
                    scroll={{ x: 1050 }}
                    onRow={(record) => ({
                        onClick: () => {
                            handleClickViewSupport(record);
                        },
                        style: {
                            cursor: "pointer",
                        },
                    })}
                    locale={{
                        emptyText: `Nenhuma mensagem de suporte (${origin}) encontrada.`,
                    }}
                />
            </TableCard>

            <TablePagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePaginationChange}
                itemName="mensagens"
                pageSizeOptions={["10", "25", "50", "100"]}
            />

            {/* Update / Respond Modal */}
            <ModalUpdateSupport
                open={isUpdateModalVisible}
                support={supportBeingUpdated}
                onClose={handleCloseUpdateModal}
                onSave={handleSaveUpdateSupport}
                isSaving={isLoadingUpdate}
            />

            {/* Delete Modal */}
            <Modal
                open={isDeleteModalVisible}
                onCancel={handleCancelDelete}
                onOk={handleConfirmDelete}
                confirmLoading={isLoadingDelete}
                okText="Sim, Excluir"
                cancelText="Cancelar"
                okType="danger"
                okButtonProps={{ loading: isLoadingDelete }}
                cancelButtonProps={{ disabled: isLoadingDelete }}
                centered
                title={
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <ExclamationCircleOutlined style={{ color: "#ff4d4f", fontSize: 20 }} />
                        <span>Confirmar Exclusão de Mensagem</span>
                    </div>
                }
            >
                <Paragraph style={{ marginTop: 12 }}>
                    Deseja realmente excluir a mensagem de suporte de{" "}
                    <strong>{supportBeingDeleted?.name || "Solicitante"}</strong>?
                </Paragraph>
                <div
                    style={{
                        background: isDark ? "#141414" : "#fff1f0",
                        border: `1px solid ${isDark ? "#262626" : "#ffa39e"}`,
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 12.5,
                    }}
                >
                    <div style={{ color: isDark ? "#bbb" : "#475569" }}>
                        <strong>Mensagem:</strong> {supportBeingDeleted?.message || "--"}
                    </div>
                </div>
            </Modal>
        </div>
    );
}
